import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/lib/supabase'
import { supabaseLogger } from '@/lib/logger'
import { getDataProvider, isStandaloneMode } from '@/lib/dataProvider'
import { runImageCleanup } from '@/services/imageCleanupService'
import type { User, Session } from '@supabase/supabase-js'

export interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  error: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const session = ref<Session | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userEmail = computed(() => user.value?.email)

  // Actions
  const clearError = () => {
    error.value = null
  }

  const setLoading = (value: boolean) => {
    loading.value = value
  }

  const setUser = (newUser: User | null) => {
    user.value = newUser
  }

  const setSession = (newSession: Session | null) => {
    const hadSession = !!session.value
    session.value = newSession
    user.value = newSession?.user ?? null
    
    // Run image cleanup on login (not on logout or session refresh)
    if (newSession && !hadSession && !isStandaloneMode()) {
      // Run in background, don't block login
      runImageCleanup().catch(err => {
        supabaseLogger.warn('[Auth] Image cleanup failed:', err)
      })
    }
  }

  const setError = (errorMessage: string) => {
    error.value = errorMessage
  }

  // Initialize auth state
  const initialize = async () => {
    try {
      setLoading(true)
      clearError()

      // Check if we're in standalone mode
      if (isStandaloneMode()) {
        supabaseLogger.info('Initializing auth in standalone mode')
        const provider = getDataProvider()
        
        // Initialize the provider (creates/loads standalone user)
        await provider.initialize()
        
        // Get auth state from provider
        const authState = provider.getAuthState()
        
        if (authState.user) {
          // Create a mock User object compatible with Supabase User type
          const standaloneUser: User = {
            id: authState.user.id,
            email: authState.user.email || undefined,
            app_metadata: {},
            user_metadata: { 
              username: authState.user.username,
              standalone: true 
            },
            aud: 'standalone',
            created_at: authState.user.createdAt,
            updated_at: authState.user.createdAt,
            role: 'authenticated',
            confirmed_at: authState.user.createdAt
          }
          user.value = standaloneUser
          supabaseLogger.info('Standalone user loaded:', authState.user.id)
        }
        
        return
      }

      // Online mode - use Supabase
      // Get initial session
      const { data: { session: initialSession } } = await supabase.auth.getSession()
      setSession(initialSession)

      // Listen for auth changes
      supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session)
      })
    } catch (err) {
      supabaseLogger.error('Error initializing auth:', err)
      setError('Failed to initialize authentication')
    } finally {
      setLoading(false)
    }
  }

  // Sign up with email and password
  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true)
      clearError()

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`
        }
      })

      if (signUpError) {
        setError(signUpError.message)
        return { success: false, error: signUpError }
      }

      return { success: true, data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      clearError()

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (signInError) {
        setError(signInError.message)
        return { success: false, error: signInError }
      }

      return { success: true, data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Sign out
  const signOut = async () => {
    try {
      setLoading(true)
      clearError()

      // In standalone mode, just clear the user and optionally switch back to online mode
      if (isStandaloneMode()) {
        user.value = null
        session.value = null
        // Note: We don't clear standalone data here, just sign out
        // User can switch back to standalone mode to access their data again
        return { success: true }
      }

      const { error: signOutError } = await supabase.auth.signOut()

      if (signOutError) {
        setError(signOutError.message)
        return { success: false, error: signOutError }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      setLoading(true)
      clearError()

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (resetError) {
        setError(resetError.message)
        return { success: false, error: resetError.message }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setLoading(true)
      clearError()

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (oauthError) {
        setError(oauthError.message)
        return { success: false, error: oauthError }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Sign in with Discord
  const signInWithDiscord = async () => {
    try {
      setLoading(true)
      clearError()

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (oauthError) {
        setError(oauthError.message)
        return { success: false, error: oauthError }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Sign in with Spotify
  const signInWithSpotify = async () => {
    try {
      setLoading(true)
      clearError()

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'spotify',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'user-read-email user-read-private'
        }
      })

      if (oauthError) {
        setError(oauthError.message)
        return { success: false, error: oauthError }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Link OAuth provider to existing account
  const linkIdentity = async (provider: 'google' | 'discord' | 'spotify') => {
    try {
      const { error: linkError } = await supabase.auth.linkIdentity({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (linkError) {
        return { success: false, error: linkError.message }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Unlink OAuth provider from account
  const unlinkIdentity = async (provider: string) => {
    try {
      const identity = user.value?.identities?.find(i => i.provider === provider)
      if (!identity) {
        return { success: false, error: 'Identity not found' }
      }

      const { error: unlinkError } = await supabase.auth.unlinkIdentity(identity)
      if (unlinkError) {
        return { success: false, error: unlinkError.message }
      }

      // Refresh user data
      await refreshUser()

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Update user metadata (like username)
  const updateUserMetadata = async (metadata: Record<string, unknown>) => {
    try {
      const { data, error: updateError } = await supabase.auth.updateUser({
        data: metadata
      })

      if (updateError) {
        return { success: false, error: updateError.message }
      }

      if (data.user) {
        setUser(data.user)
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Update email address
  const updateEmail = async (newEmail: string) => {
    try {
      const { error: updateError } = await supabase.auth.updateUser({ 
        email: newEmail 
      })

      if (updateError) {
        return { success: false, error: updateError.message }
      }

      return { success: true, message: 'Confirmation email sent!' }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Update password (requires current password verification)
  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      // First verify current password
      const email = user.value?.email
      if (!email) {
        return { success: false, error: 'No email found for current user' }
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword
      })

      if (signInError) {
        return { success: false, error: 'Current password is incorrect' }
      }

      // Now update to new password
      const { error: updateError } = await supabase.auth.updateUser({ 
        password: newPassword 
      })

      if (updateError) {
        return { success: false, error: updateError.message }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Update password directly (for password reset flow)
  const setNewPassword = async (newPassword: string) => {
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (updateError) {
        return { success: false, error: updateError.message }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Delete user account
  const deleteAccount = async () => {
    try {
      const { error: deleteError } = await supabase.functions.invoke('delete-user-account')

      if (deleteError) {
        return { success: false, error: deleteError.message }
      }

      // Sign out after successful deletion
      await signOut()

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Refresh user data from server
  const refreshUser = async () => {
    try {
      const { data, error: getError } = await supabase.auth.getUser()

      if (getError) {
        return { success: false, error: getError.message }
      }

      if (data.user) {
        setUser(data.user)
      }

      return { success: true, user: data.user }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Get current session
  const getSession = async () => {
    try {
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession()

      if (sessionError) {
        return { success: false, error: sessionError.message }
      }

      return { success: true, session: currentSession }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  return {
    // State
    user,
    session,
    loading,
    error,
    
    // Getters
    isAuthenticated,
    userEmail,
    
    // Actions
    initialize,
    signUp,
    signIn,
    signOut,
    resetPassword,
    signInWithGoogle,
    signInWithDiscord,
    signInWithSpotify,
    linkIdentity,
    unlinkIdentity,
    updateUserMetadata,
    updateEmail,
    updatePassword,
    setNewPassword,
    deleteAccount,
    refreshUser,
    getSession,
    clearError,
    setLoading,
    setUser,
    setSession,
    setError
  }
})